import { Anchor, Box, LoadingSpinner, PageGridContainer, SimpleGrid, Text } from "@govlaunch/web";
import { NetworkStatus, useQuery } from "@apollo/client";
import moment from "moment";
import React, { SyntheticEvent } from "react";
import { FormattedMessage } from "react-intl";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";
import MyCollectionsIcon from "~/components/icons/Collections/MyCollectionsIcon";
import { IToggleBookmarkMutationVariables } from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import { IBookmarkType, ICollectionPackaging } from "~/types/types";
import stringOrFirst from "~/src/utils/stringOrFirst";
import BundleCollectionCard from "~/src/components/collection/bundleCard/BundleCollectionCard";
import { ICardViewMode } from "~/src/components/collection/cardViewMode";
import CollectionCard from "~/src/components/collection/collectionCard/CollectionCard";
import SelfieCollectionsQuery from "~/src/components/collection/graphql/SelfieCollectionsQuery";
import {
  ISelfieCollectionsQuery,
  ISelfieCollectionsQueryVariables,
} from "~/src/components/collection/graphql/__generated__/SelfieCollectionsQuery.generated";
import NoSavedCollections from "~/src/components/collection/NoSavedCollections";

export const MY_COLLECTIONS_ID = "my_collections";

interface IMyCollectionsProps {
  query: string | string[];
  toggleBookmark: (bookmarked: boolean, params: { variables: IToggleBookmarkMutationVariables }) => void;
  isMobile?: boolean;
}

export default function MyCollections({ query, toggleBookmark, isMobile = false }: IMyCollectionsProps) {
  // NOTE: do not touch
  const lastVisitDate = moment().subtract(7, "days").format("YYYY-MM-DD");

  const { data, loading, fetchMore, networkStatus } = useQuery<
    ISelfieCollectionsQuery,
    ISelfieCollectionsQueryVariables
  >(SelfieCollectionsQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      searchQuery: stringOrFirst(query),
      lastVisitDate: `${lastVisitDate}T00:00:00Z`,
      cursor: null,
    },
  });

  if ((loading || networkStatus === NetworkStatus.loading) && !data?.listAllCollections.items.length) {
    return (
      <Box p="84px" justifyContent="center" display="flex">
        <LoadingSpinner />
      </Box>
    );
  }

  if (!data || !data.listAllCollections || data.listAllCollections.items.length === 0) {
    return (
      <PageGridContainer px={6}>
        <NoSavedCollections />
      </PageGridContainer>
    );
  }

  return (
    <main data-intercom-target="My collections" id={MY_COLLECTIONS_ID}>
      <PageGridContainer px={6}>
        <div>
          <Text fontSize="xl" display="flex" fontWeight="bold" color="blue.600" alignItems="center">
            <MyCollectionsIcon />
            <span
              css={{
                marginLeft: 16,
                textTransform: "uppercase",
              }}
            >
              <FormattedMessage
                defaultMessage="My Collections ({totalCount})" id="ymf3bk"
                values={{
                  totalCount: data.listAllCollections.totalCount,
                }}
              />
            </span>
          </Text>
        </div>

        <Box mt={8}>
          <SimpleGrid columns={[1, 2, 3, 4]} spacingX={[6, 6, 6, "86px"]} spacingY="44px" mr={[0, 0, 0, 12]}>
            {data.listAllCollections.items.map((collection, index) => {
              if (collection.packaging === ICollectionPackaging.Single) {
                return (
                  <CollectionCard
                    key={collection._id}
                    collection={collection}
                    viewMode={isMobile ? ICardViewMode.Mobile : ICardViewMode.MyCollections}
                    variant={(index % 3) + 1}
                    onToggleBookmark={({ _id }) =>
                      toggleBookmark(!collection.viewerDidBookmark, {
                        variables: {
                          targetId: _id,
                          targetType: IBookmarkType.Collection,
                        },
                      })
                    }
                  />
                );
              }

              if (collection.packaging === ICollectionPackaging.Bundle) {
                return (
                  <BundleCollectionCard
                    viewMode={isMobile ? ICardViewMode.Mobile : ICardViewMode.MyCollections}
                    key={collection._id}
                    collection={collection}
                    onToggleBookmark={({ _id }) =>
                      toggleBookmark(!collection.viewerDidBookmark, {
                        variables: {
                          targetId: _id,
                          targetType: IBookmarkType.Collection,
                        },
                      })
                    }
                  />
                );
              }

              return null;
            })}
          </SimpleGrid>

          {data.listAllCollections.pageInfo.hasNextPage && (
            <Box display="flex" justifyContent="center" mt={6}>
              <ShowMoreButtonWithIcon
                loading={loading}
                onClick={() => {
                  fetchMore({
                    variables: {
                      cursor: data?.listAllCollections.pageInfo.cursor,
                    },
                    updateQuery: (current, { fetchMoreResult }) => {
                      if (!fetchMoreResult || !fetchMoreResult.listAllCollections || !current.listAllCollections) {
                        return {
                          __typename: "Query",
                          listAllCollections: current.listAllCollections,
                        };
                      }

                      return {
                        __typename: "Query",
                        listAllCollections: {
                          ...current.listAllCollections,
                          items: current.listAllCollections.items.concat(fetchMoreResult.listAllCollections.items),
                          pageInfo: {
                            ...fetchMoreResult.listAllCollections.pageInfo,
                          },
                        },
                      };
                    },
                  });
                }}
              />
            </Box>
          )}
        </Box>
      </PageGridContainer>
    </main>
  );
}

export function ScrollToMyCollections() {
  return (
    <Anchor
      ml="auto"
      mb={2}
      onClick={(event: SyntheticEvent) => {
        const node = document.getElementById(MY_COLLECTIONS_ID);

        if (node) {
          node.scrollIntoView({
            behavior: "smooth",
          });
        }

        event.preventDefault();
      }}
    >
      <FormattedMessage defaultMessage="My Collections" id="Hbk7yA" /> &gt;&gt;
    </Anchor>
  );
}
